import { useParams } from 'react-router-dom';
import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { HotTable } from '@handsontable/react';
import 'handsontable/dist/handsontable.full.min.css';
import '../../../styles/handsontable.css'; // Import generic HandsOnTable styles
import { Box, Flex, useToast, Button, Text, HStack, useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton } from '@chakra-ui/react';
import SubmitButton from '../../../CommonComponents/SubmitButton';
import { DataNotFound } from '../../../CommonComponents/DataNotFound';
import FormLoader from '../../../CommonComponents/FormLoader';
import Pagination from '../../DeriveFile/Pagination';
import { getPersonalityExhibitionsData, updatePersonalityExhibitionsData, deletePersonalityExhibitionsData } from '../../../services/Personality';
import { showDeletedToast, showErrorToast, showInfoToast, showSuccessToast } from '../../../util/errorHandler';


const Exhibitions = (props) => {
  const { alias } = useParams();
  const [columnHeaders, setColumnHeaders] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [changedRecords, setChangedRecords] = useState(new Map()); // Track changes by row index
  const [deletingRows, setDeletingRows] = useState(new Set()); // Track rows being deleted
  const [rowToDelete, setRowToDelete] = useState(null); // Track which row to delete

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalRecords, setTotalRecords] = useState(0);
  const [allData, setAllData] = useState([]); // Store all data for pagination
  
  const hotRef = useRef(null);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Handle individual cell changes in HandsOnTable
  const handleTableChange = useCallback((changes, source) => {
    if (source === 'edit' && changes) {
      const newChangedRecords = new Map(changedRecords);
      
      changes.forEach(([row, col, oldValue, newValue]) => {
        // Skip S.No. column (col === 0), ID column (col === 1), and Actions column
        if (col === 0 || col === 1 || col === columnHeaders.length + 1) return;
        
        // Only track if value actually changed
        if (oldValue !== newValue) {
          const rowKey = row;
          const columnHeader = columnHeaders[col - 1]; // Adjust index due to S.No. column
          
          if (!newChangedRecords.has(rowKey)) {
            // Initialize record with ID and changed field
            newChangedRecords.set(rowKey, {
              [columnHeaders[0]]: tableData[row][1], // ID field (now at index 1)
              [columnHeader]: newValue
            });
          } else {
            // Add/update field in existing changed record
            const existingRecord = newChangedRecords.get(rowKey);
            existingRecord[columnHeader] = newValue;
            newChangedRecords.set(rowKey, existingRecord);
          }
        }
      });
      
      setChangedRecords(newChangedRecords);
    }
  }, [changedRecords, columnHeaders, tableData]);

  // Pagination utility functions
  const getCurrentPageData = useCallback(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return allData.slice(startIndex, endIndex);
  }, [currentPage, pageSize, allData]);

  const getTotalPages = useCallback(() => {
    return Math.ceil(totalRecords / pageSize);
  }, [totalRecords, pageSize]);

  const handlePageChange = useCallback((newPage) => {
    if (newPage >= 1 && newPage <= getTotalPages()) {
      setCurrentPage(newPage);
      
      // Update current page data
      const newPageData = getCurrentPageData();
      setTableData(newPageData);
      setOriginalData(newPageData);
    }
  }, [getTotalPages, getCurrentPageData]);


  useEffect(() => {
    const getExhibitionsInitialData = async () => {
      try {
        if (alias) {
          setIsLoading(true);
          const result = await getPersonalityExhibitionsData(alias);
          if (result && result.data && result.data.length > 0) {
            const keys = Object.keys(result.data[0]);
            setColumnHeaders(keys);

            const rowData = result.data.map((item) => [
              null, // Placeholder for S.No. column (will be calculated dynamically)
              ...keys.map((key) => item[key] ?? ""), // Extract values by key
              null // Add placeholder for Actions column
            ]);
            
            // Store all data for pagination
            setAllData(rowData);
            setTotalRecords(rowData.length);
            
            // Set current page data
            const currentPageData = rowData.slice(0, pageSize);
            setTableData(currentPageData);
            setOriginalData(currentPageData); // Store original data for comparison
          }
        }
      } catch (error) {
        console.error('Error fetching exhibitions data:', error);
                showErrorToast(toast, "Failed to fetch data. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    getExhibitionsInitialData();
  }, [alias, pageSize, toast]);

  // Update table data when pagination changes
  useEffect(() => {
    if (allData.length > 0) {
      const currentPageData = getCurrentPageData();
      setTableData(currentPageData);
      setOriginalData(currentPageData);
    }
  }, [currentPage, pageSize, allData, getCurrentPageData]);

  const saveClickCallback = useCallback(async () => {
    // Check if there are any changes to save
    if (changedRecords.size === 0) {
            showInfoToast(toast, "No changes to save");
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Convert Map to array of changed records
      const updatedRecords = Array.from(changedRecords.values());

      const result = await updatePersonalityExhibitionsData(alias, updatedRecords);

      if (result) {
                showSuccessToast(toast, "Data saved successfully");
        
        // Clear the changed records after successful save
        setChangedRecords(new Map());
        
        // Update the original data to reflect the new state
        const hot = hotRef.current.hotInstance;
        const currentData = hot.getData();
        setOriginalData(currentData);
        
        // Update the table data to reflect changes immediately
        setTableData(currentData);
        
        // Update the corresponding data in allData to maintain consistency across pages
        const updatedAllData = [...allData];
        const startIndex = (currentPage - 1) * pageSize;
        currentData.forEach((row, index) => {
          updatedAllData[startIndex + index] = row;
        });
        setAllData(updatedAllData);
      }
    } catch (error) {
      console.error("Error saving data:", error);
            showErrorToast(toast, "Failed to save data. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }, [changedRecords, alias, currentPage, pageSize, allData, toast]);

  // Delete functionality
  const handleDeleteRow = useCallback(async (rowIndex) => {
    const rowData = tableData[rowIndex];
    const recordId = rowData[1]; // ID is now at index 1 (after S.No. column)
    
    if (!recordId) {
            showErrorToast(toast, "Cannot delete record without ID");
      return;
    }

    // Add row to deleting state
    setDeletingRows(prev => new Set(prev).add(rowIndex));
    
    try {
      const result = await deletePersonalityExhibitionsData(alias, [{ [columnHeaders[0]]: recordId }]);
      if (result) {
        // Calculate the global index of the deleted row
        const globalRowIndex = (currentPage - 1) * pageSize + rowIndex;
        
        // Remove row from all data sources
        const newAllData = allData.filter((_, index) => index !== globalRowIndex);
        
        const newTableData = tableData.filter((_, index) => index !== rowIndex);
        const newOriginalData = originalData.filter((_, index) => index !== rowIndex);
        
        // Update all states
        setAllData(newAllData);
        setTableData(newTableData);
        setOriginalData(newOriginalData);
        setTotalRecords(prev => prev - 1);
        
        // Clear any changes for this row
        const newChangedRecords = new Map(changedRecords);
        newChangedRecords.delete(rowIndex);
        setChangedRecords(newChangedRecords);
        
                showDeletedToast(toast);
        
        // Check if current page is now empty and adjust
        if (newTableData.length === 0 && currentPage > 1) {
          handlePageChange(currentPage - 1);
        }
      } else {
        throw new Error("Delete operation failed");
      }
    } catch (error) {
      console.error("Error deleting record:", error);
            showErrorToast(toast, "Failed to delete record. Please try again.");
    } finally {
      // Remove row from deleting state
      setDeletingRows(prev => {
        const newSet = new Set(prev);
        newSet.delete(rowIndex);
        return newSet;
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData, alias, columnHeaders, currentPage, pageSize, allData, changedRecords, handlePageChange, toast]);

  // Delete confirmation dialog
  const confirmDelete = useCallback((rowIndex) => {
    const rowData = tableData[rowIndex];
    const recordId = rowData[1]; // ID is now at index 1
    
    setRowToDelete({ rowIndex, recordId, rowData });
    onOpen();
  }, [tableData, onOpen]);

  // Handle actual deletion after confirmation
  const handleConfirmedDelete = useCallback(() => {
    if (rowToDelete) {
      handleDeleteRow(rowToDelete.rowIndex);
      onClose();
      setRowToDelete(null);
    }
  }, [rowToDelete, onClose, handleDeleteRow]);

  // Custom renderer for delete column
  const deleteColumnRenderer = useCallback((instance, td, row, col, prop, value, cellProperties) => {
    // Clear the cell
    td.innerHTML = '';
    
    // Create delete button
    const deleteButton = document.createElement('button');
    const isDeleting = deletingRows.has(row);
    
    if (isDeleting) {
      deleteButton.innerHTML = '⏳'; // Loading indicator
      deleteButton.title = 'Deleting...';
      deleteButton.className = 'delete-button deleting';
    } else {
      deleteButton.innerHTML = '🗑️'; // Delete icon
      deleteButton.title = 'Delete this record';
      deleteButton.className = 'delete-button';
    }
    
    // Add click handler
    deleteButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!isDeleting) {
        confirmDelete(row);
      }
    });
    
    // Append button to cell
    td.appendChild(deleteButton);
    
    return td;
  }, [deletingRows, confirmDelete]);

  const hotTableSettings = useMemo(() => ({
    data: tableData,
    colHeaders: ['S.No.', ...columnHeaders, 'Actions'], // Add S.No. as first column
    height: "auto",
    afterChange: handleTableChange, // Track changes in real-time
    licenseKey: "non-commercial-and-evaluation",
    autoWrapRow: true,
    contextMenu: true,
    cells: (row, col) => {
      if (col === 0) return { readOnly: true }; // S.No. column is read-only
      if (col === 1) return { readOnly: true }; // ID column is read-only
      if (col === columnHeaders.length + 1) return { renderer: deleteColumnRenderer }; // Delete column
      
      // Make only alias column read-only
      const columnHeader = columnHeaders[col - 1]; // Adjust index due to S.No. column
      if (columnHeader === 'alias') {
        return { readOnly: true };
      }
      
      return {}; // Default for other columns (editable)
    },
    hiddenColumns: {
      columns: [1], // Hide the ID column (index 1) instead of index 0
      indicators: false, // No visual indicators for hidden columns
    },
    plugins: ["hiddenColumns"],
    columns: [
      // S.No. column
      {
        data: 0,
        title: 'S.No.',
        width: 60,
        readOnly: true,
        renderer: (instance, td, row, col, prop, value, cellProperties) => {
          // Calculate serial number based on current page and row
          const serialNumber = (currentPage - 1) * pageSize + row + 1;
          td.innerHTML = serialNumber;
          td.style.textAlign = 'center';
          td.style.fontWeight = 'bold';
          td.style.backgroundColor = '#f8f9fa';
          td.style.borderRight = '2px solid #dee2e6';
          td.style.color = '#495057';
          td.style.height = '30px';
          return td;
        }
      },
      // Map existing columns (shifted by 1 due to S.No. column)
      ...columnHeaders.map((header, index) => {
        const columnConfig = {
          data: index + 1, // Shift data index by 1
          title: header,
          readOnly: index === 0 || header === 'alias' // ID and alias columns are read-only
        };
        
        // Calculate width based on header text length or max 300px
        const headerTextWidth = header.length * 8 + 140; // Approximate character width + padding
        const columnWidth = Math.min(headerTextWidth, 300);
        
        // Add width and text wrapping configuration for all columns
        columnConfig.width = columnWidth;
        columnConfig.wordWrap = true;
        columnConfig.renderer = (instance, td, row, col, prop, value, cellProperties) => {
          // Set cell properties for text wrapping
          td.style.whiteSpace = 'normal';
          td.style.wordWrap = 'break-word';
          td.style.maxWidth = `${columnWidth}px`;
          td.style.height = 'auto';
          td.style.padding = '8px';
          td.style.verticalAlign = 'top';
          
          // Set the cell content
          td.innerHTML = value || '';
          return td;
        };
        
        return columnConfig;
      }),
      // Add delete column
      {
        data: columnHeaders.length + 1,
        title: 'Actions',
        width: 80,
        renderer: deleteColumnRenderer
      }
    ],
    // Row styling for deleting rows
    beforeRenderer: (row, col, prop, value, cellProperties) => {
      if (deletingRows.has(row)) {
        cellProperties.className = 'deleting-row';
      }
    }
  }), [tableData, columnHeaders, handleTableChange, deleteColumnRenderer, currentPage, pageSize, deletingRows]);

  return (
    <Box maxW="100%">
      {isLoading ? (
        <FormLoader />
      ) : (
        <>
    {allData?.length > 0 ? (
      <>
          <Flex justifyContent="space-between" mb={4} alignItems="center">
            <SubmitButton isLoading={isSubmitting} onClick={saveClickCallback}/>
            <HStack spacing={4}>
              {changedRecords.size > 0 && (
                <Box color="orange.500" fontSize="sm">
                  {changedRecords.size} record(s) modified - Click Save to update
                </Box>
              )}
              {deletingRows.size > 0 && (
                <Box color="red.500" fontSize="sm">
                  {deletingRows.size} record(s) being deleted...
                </Box>
              )}
            </HStack>
          </Flex>
          
          {/* Pagination at top right */}
          {totalRecords > pageSize && (
            <HStack spacing={4} justifyContent="flex-end" mb={4}>
              <Pagination 
                handlePageChange={handlePageChange} 
                currentPage={currentPage} 
                totalPages={getTotalPages()} 
                //Addded handlePageSizeChange just to remove lint error
                setPageSize={setPageSize}
              />
            </HStack>
          )}
          
    <HotTable
      ref={hotRef}
      {...hotTableSettings}
                />
        
        <Flex justifyContent="space-between" mt={4} alignItems="center">
          <SubmitButton isLoading={isSubmitting} onClick={saveClickCallback}/>
        </Flex>
    </>
    ) : (
      <DataNotFound />
    )}
    </>
      )}
      
      {/* Delete Confirmation Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Delete</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              Are you sure you want to delete this record?
            </Text>
            {rowToDelete && (
              <Box mt={3} p={3} bg="gray.50" borderRadius="md">
                <Text fontSize="sm" fontWeight="bold">Record Details:</Text>
                <Text fontSize="sm">ID: {rowToDelete.recordId}</Text>
                {/* Show first few fields for identification (adjust indices for S.No. column) */}
                {columnHeaders.slice(1, 4).map((header, index) => (
                  <Text key={index} fontSize="sm">
                    {header}: {rowToDelete.rowData[index + 2] || 'N/A'}
                  </Text>
                ))}
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="red" 
              onClick={handleConfirmedDelete}
              isLoading={rowToDelete && deletingRows.has(rowToDelete.rowIndex)}
            >
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Exhibitions;
